import { useNavigate } from 'react-router'
import clsx from 'clsx'
import { AspectRatio, Table, Image } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconArrowDown, IconArrowUp, IconPoint } from '@tabler/icons-react'
import { getRankSuffix } from '../../common/helpers'
import { Standings } from '../../ladder/api'
import { useCompetitionId } from '../../common/hooks'
import { Typography } from '../../common/typography'
import classes from './Ladder.module.css'

export default function RacesLadderComponent({
  stats,
  active,
  divisionLadder,
}: {
  stats: Standings[]
  active?: string
  divisionLadder: boolean
}) {
  const navigate = useNavigate()
  const competitionId = useCompetitionId()
  const isMobile = useMediaQuery('(max-width: 800px)')
  const short = active === 'full' || active === 'short' || !active
  const form = active === 'form' || active === 'full' || !active
  const shouldStickFirstColumn = isMobile && active === 'full'

  const handleRowClick = (teamId: number) => {
    navigate(`/competitions/${competitionId}/teams/${teamId}/overview`)
  }

  const sortedStats = [...stats].sort((a, b) => {
    if (divisionLadder) {
      return (a.division_rank ?? 0) - (b.division_rank ?? 0)
    }
    return a.rank - b.rank
  })

  const rows = sortedStats.map((ts) => {
    const rank = divisionLadder ? (ts.division_rank ?? 0) : ts.rank
    const change = divisionLadder
      ? (ts.division_rank_change ?? 0)
      : ts.rank_change

    return (
      <Table.Tr key={ts.team_id} onClick={() => handleRowClick(ts.team_id)}>
        <Table.Td>
          <div className={classes.team}>
            <Typography lh="150" ta="left">
              {rank}
              <sup>{getRankSuffix(rank)}</sup>
            </Typography>
            {change === 0 && (
              <div className={classes.rank_update}>
                <IconPoint className={classes.icon} />
              </div>
            )}
            {change > 0 && (
              <div className={classes.rank_update}>
                <IconArrowUp
                  className={clsx(classes.icon, classes.up, {
                    [classes.iconMobile]: isMobile,
                  })}
                />
                <Typography
                  fs="sm"
                  ff="JetBrains Mono"
                  fw={500}
                  ta="center"
                  className={classes.up}
                >
                  {`+${change}`}
                </Typography>
              </div>
            )}
            {change < 0 && (
              <div className={classes.rank_update}>
                <IconArrowDown
                  className={clsx(classes.icon, classes.down, {
                    [classes.iconMobile]: isMobile,
                  })}
                />
                <Typography
                  fs="sm"
                  ff="JetBrains Mono"
                  fw={500}
                  ta="center"
                  className={classes.down}
                >
                  {change}
                </Typography>
              </div>
            )}
          </div>
        </Table.Td>
        <Table.Td>
          <AspectRatio ratio={1 / 1} className={classes.teamLogoCell}>
            <Image
              src={ts.team_logo.thumbnail}
              alt={ts.team_name}
              className={clsx(classes.teamLogo, {
                [classes.teamLogoMobile]: isMobile,
              })}
              fit="scale-down"
            />
          </AspectRatio>
        </Table.Td>
        <Table.Td className={classes.teamName}>
          <Typography lh="150">{ts.team_name}</Typography>
        </Table.Td>
        {short && (
          <Table.Td>
            <Typography ff="JetBrains Mono" lh="140" ta="center">
              {ts.points}
            </Typography>
          </Table.Td>
        )}
        {(active === 'full' || !active) && (
          <>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.avg_race_score.toFixed(2)}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.starts}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.wins}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.top_3}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.top_5}
              </Typography>
            </Table.Td>
          </>
        )}
        {form && (
          <Table.Td>
            <div className={classes.formWrapper}>
              {ts.form.map((form, index) => (
                <div
                  key={index}
                  className={clsx(
                    classes.formContainer,
                    { [classes.firstPlace]: Number(form) === 1 },
                    { [classes.secondPlace]: Number(form) === 2 },
                    { [classes.thirdPlace]: Number(form) === 3 }
                  )}
                >
                  <Typography
                    fw={600}
                    fc={{ light: 'white', dark: 'white' }}
                    lh="130"
                    ta="center"
                  >
                    {form}
                    <sup>{getRankSuffix(Number(form))}</sup>
                  </Typography>
                </div>
              ))}
            </div>
          </Table.Td>
        )}
      </Table.Tr>
    )
  })

  return (
    <div className={classes.tableContainer}>
      <Table.ScrollContainer minWidth={300}>
        <Table
          classNames={{
            table: clsx(
              classes.table,
              shouldStickFirstColumn && classes.stickyFirstColumn
            ),
            tr: classes.tr,
            th: classes.th,
            td: classes.td,
          }}
          verticalSpacing="xs"
          withRowBorders={false}
        >
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              <Table.Th>
                <Typography
                  fw={600}
                  lh="140"
                  ta="left"
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                >
                  #
                </Typography>
              </Table.Th>
              <Table.Th></Table.Th>
              <Table.Th>
                <Typography
                  fw={600}
                  lh="140"
                  ta="left"
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                >
                  Team
                </Typography>
              </Table.Th>
              {short && (
                <Table.Th>
                  <Typography
                    fw={600}
                    lh="140"
                    ta="center"
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                  >
                    Points
                  </Typography>
                </Table.Th>
              )}

              {(active === 'full' || !active) && (
                <>
                  <Table.Th>
                    <Typography
                      fw={600}
                      lh="140"
                      ta="center"
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                    >
                      Avg Race Rating
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fw={600}
                      lh="140"
                      ta="center"
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                    >
                      Starts
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fw={600}
                      lh="140"
                      ta="center"
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                    >
                      Wins
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fw={600}
                      lh="140"
                      ta="center"
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                    >
                      Top 3
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fw={600}
                      lh="140"
                      ta="center"
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                    >
                      Top 5
                    </Typography>
                  </Table.Th>
                </>
              )}
              {form && (
                <Table.Th>
                  <Typography
                    fw={600}
                    lh="140"
                    ta="center"
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                  >
                    Form
                  </Typography>
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
